import { useRef, useState } from 'react'
import type { Contact } from '@/types/api'
import { useCompanyLogo } from '@/hooks/useCompanyLogo'
import { useInView } from '@/hooks/useInView'
import { companyInitials, companyLogoDomain, contactCompanyName } from '@/lib/company'
import { cn } from '@/lib/utils'

interface CompanyLogoProps {
  name: string
  domain: string | null
  className?: string
}

/**
 * Logo of a company, loaded lazily once it nears the viewport; the initials of
 * the name stand in while it loads, when there is none, or if it fails to load.
 * Decorative: the company name is always shown next to it.
 */
export function CompanyLogo({ name, domain, className }: CompanyLogoProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref)
  const { data: src } = useCompanyLogo(domain, inView)
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null)
  const showImage = src && src !== brokenSrc

  return (
    <span
      ref={ref}
      aria-hidden
      className={cn(
        'inline-flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded bg-muted text-[10px] font-semibold text-muted-foreground',
        className,
      )}
    >
      {showImage ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-contain"
          onError={() => setBrokenSrc(src)}
        />
      ) : (
        companyInitials(name)
      )}
    </span>
  )
}

/** Company of a contact with its logo, or "-" when the contact has no company. */
export function ContactCompany({ contact }: { contact: Pick<Contact, 'email' | 'company' | 'properties'> }) {
  const name = contactCompanyName(contact)
  if (!name) return <>-</>
  return (
    <span className="inline-flex items-center gap-2">
      <CompanyLogo name={name} domain={companyLogoDomain(contact)} />
      <span>{name}</span>
    </span>
  )
}
